import * as React from 'react';
import Space from 'antd/lib/space';
import Text from 'antd/lib/typography/Text';
import { ClockCircleOutlined } from '@ant-design/icons';
import { ReleaseStepStoreReviewData, ReleaseStepSubmissionData, ReviewAttachmentUploadStatus } from '@/types';
import {
    ArchetypeSpecificStepRunSummaryPanelProps,
    StepRunSummaryContainer,
    StepRunSummaryDetailItem,
    StepRunSummaryDetailSeparator,
    AppReviewInformationDetailItem,
    useSRRTopBarData,
    createSRRTopBar,
} from '.';
import { antGrey } from '@/components/styles/globalStyles';
import { Nullable } from '@/typescriptHelpers';
import { timeStringFromDuration } from '@/helpers';

interface ReviewDurationDetailItemProps {
    durationInSeconds: Nullable<number>;
    header: string;
}

const ReviewDurationDetailItem: React.FC<ReviewDurationDetailItemProps> = ({ durationInSeconds, header }) => {
    if (durationInSeconds == null) {
        return null;
    }

    return (
        <StepRunSummaryDetailItem
            header={header}
            body={
                <Space size={8} align="center">
                    <ClockCircleOutlined style={{ color: antGrey.grey6 }} />
                    <Text style={{ color: antGrey.grey8 }}>{timeStringFromDuration(durationInSeconds)}</Text>
                </Space>
            }
        />
    );
};

export const StoreReviewStepRunSummaryPanel: React.FC<ArchetypeSpecificStepRunSummaryPanelProps> = ({ stepRun }) => {
    const { stepData, appStoreData, isSubmitted, appStoreState, hasSelectedBuild } = useSRRTopBarData(stepRun);
    const storeReviewData = stepData as ReleaseStepStoreReviewData;
    const reviewDetail = appStoreData?.reviewDetail;
    const reviewNotes = reviewDetail?.notes;
    const completedAttachments = appStoreData?.reviewDetail?.reviewAttachments?.filter(
        (attachment) => attachment.status === ReviewAttachmentUploadStatus.Complete,
    );

    const topBar = createSRRTopBar(
        stepRun,
        stepData as ReleaseStepSubmissionData,
        appStoreData,
        isSubmitted,
        appStoreState,
        hasSelectedBuild,
    );

    const hasTimeWaitingForReview = !!storeReviewData?.timeWaitingForReview;
    const hasTimeInReview = !!storeReviewData?.timeInReview;

    return (
        <div>
            <StepRunSummaryContainer topBar={topBar}>
                {hasTimeWaitingForReview && (
                    <>
                        <ReviewDurationDetailItem
                            durationInSeconds={storeReviewData.timeWaitingForReview}
                            header="TIME WAITING FOR REVIEW"
                        />
                        <StepRunSummaryDetailSeparator />
                    </>
                )}
                {hasTimeInReview && (
                    <>
                        <ReviewDurationDetailItem
                            durationInSeconds={storeReviewData.timeInReview}
                            header="TIME IN REVIEW"
                        />
                        <StepRunSummaryDetailSeparator />
                    </>
                )}
                <AppReviewInformationDetailItem
                    shouldDisplayCompactVersion
                    reviewNotes={reviewNotes}
                    attachments={completedAttachments}
                    contactFirstName={reviewDetail?.contactFirstName}
                    contactLastName={reviewDetail?.contactLastName}
                    contactEmail={reviewDetail?.contactEmail}
                    contactPhone={reviewDetail?.contactPhone}
                    demoAccountRequired={reviewDetail?.demoAccountRequired}
                />
            </StepRunSummaryContainer>
        </div>
    );
};
